import ModernApiController from "@/api/ModernApiController";
import { IsProblemResult } from "@/helpers/newUserSelector";
import { PagedResult, Portal, UserGroup, UserGroupType } from "@infoland/iprova-api";
import { UserGroupFilterPostDto } from "@/api/UserGroupFilterPostDto";
import { UserGroupGetDto } from "@/api/UserGroupGetDto";
import { UserGroupTypeDto } from "@/api/UserGroupTypeDto";
import { UserMini } from "@/api/UserMini";

//Somehow the UserGroupMemberGetDto is not exported from the api, so I have to redefine it here
export type UserGroupMemberGetDto = UserMini & {
    user_groups: Pick<UserGroupGetDto, "user_group_id" | "name">[];
};

export default {
    /**
     * Gets a list of usergroups
     * @param filter (optional) A filter to use to find only specific usergroups
     * @param offset (optional, default 0) The offset from which to start returning usergroups from the list
     * @param maxResults (optional, default 100) The maximum usergroups to return at one time
     * @param suppressErrors Whether to supress the throwing of exceptions on error
     * @returns a paged result of usergroups
     */
    async getUserGroupsAsync(
        filter: UserGroupFilterPostDto = undefined,
        offset: number = 0,
        maxResults: number = 100,
        suppressErrors: boolean = false
    ): Promise<PagedResult<UserGroup> | null> {
        let path: string;

        if (filter && filterRequiresPost(filter)) {
            const filterResponse: any = await ModernApiController.postAsync<UserGroupFilterPostDto, any>(
                "/user_groups/filter",
                filter,
                suppressErrors
            );

            if (!IsProblemResult(filterResponse)) {
                const createdIdentifier: string = filterResponse.created_identifier;
                path = `/user_groups/filter/${createdIdentifier}?offset=${offset}&limit=${maxResults}&envelope=false`;
            } else return null;
        } else {
            path = `/user_groups?offset=${offset}&limit=${maxResults}&envelope=false`;

            if (filter != undefined) {
                // add simple filters to the path
                if (filter.name_contains) path += `&name_contains=${encodeURIComponent(filter.name_contains)}`;
                if (filter.group_types != undefined) path += `&group_types=${filter.group_types.join(",")}`;
            }
        }

        const userGroupResponse: PagedResult<UserGroupGetDto> = await ModernApiController.getPagedResultAsync<UserGroupGetDto>(path, suppressErrors);

        // If the response is a problem, return null
        if (IsProblemResult(userGroupResponse)) return null;

        const userGroupTypeMapping: Record<UserGroupTypeDto, UserGroupType> = {
            system: UserGroupType.system_user_group,
            synced: UserGroupType.synced_user_group,
            normal: UserGroupType.normal_user_group,
            inactive: UserGroupType.disabled_user_group
        };

        const mappedUserGroups: UserGroup[] = userGroupResponse.items.map((userGroup: UserGroupGetDto) => ({
            user_group_id: userGroup.user_group_id,
            name: userGroup.name,
            description: userGroup.description,
            user_group_type: userGroupTypeMapping[userGroup.user_group_type] || UserGroupType.disabled_user_group,
            start_portal: userGroup.start_portal as Portal
        }));

        const mappedUserGroupResponse: PagedResult<UserGroup> = { ...userGroupResponse, items: mappedUserGroups };

        return mappedUserGroupResponse;
    },

    async getUserGroupMembersAsync(userGroupId: string, suppressErrors: boolean = false): Promise<UserGroupMemberGetDto[] | null> {
        const path: string = `/user_groups/members?userGroupIds=${userGroupId}`;
        const userGroupMembersResponse: UserGroupMemberGetDto[] = await ModernApiController.getAsync<UserGroupMemberGetDto[]>(path, suppressErrors);

        if (IsProblemResult(userGroupMembersResponse)) return null;

        return userGroupMembersResponse;
    }
};

/**
 * Checks whether the given filter requires that a 'post' filter be used (because of url length limitations)
 * @param filter The filter to check
 * @returns true if the filter requires a POST request to be done first, otherwise false
 */
function filterRequiresPost(filter: UserGroupFilterPostDto): boolean {
    // check if any arrays are empty. If so, they should not go over the url
    if (filter) {
        const keys: (keyof UserGroupFilterPostDto)[] = Object.keys(filter) as (keyof UserGroupFilterPostDto)[];
        for (let k: number = 0; k < keys.length; k++) {
            const prop = filter[keys[k]];
            if (Array.isArray(prop) && prop.length == 0) return true;
        }
    }

    // require a post for every filter with usergroup ids
    if (filter.user_group_ids) return true;

    // if the name filter is longer than 50 characters, require a post
    if ((filter.name_contains || "").length > 50) return true;

    // all other cases do not require a post filter
    return false;
}
